import React, { useEffect, useState } from "react";
import { Gift, Percent, Users, ShieldCheck, ToggleLeft, ToggleRight, Edit2, Plus, X, Search, Copy } from "lucide-react";
import { dataApi, formatApiError } from "../api/client";

/**
 * PromotionsView — Módulo de promociones con anti-fraude.
 * Por ahora solo hay una promo tipo "welcome_first_ride" activa a la vez.
 */
export default function PromotionsView() {
  const [promos, setPromos] = useState([]);
  const [busy, setBusy] = useState(true);
  const [editing, setEditing] = useState(null);
  const [claimsOf, setClaimsOf] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    setBusy(true);
    try {
      const list = await dataApi.listPromotions();
      setPromos(list);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleActive = async (p) => {
    try {
      await dataApi.updatePromotion(p.id, { ...p, is_active: !p.is_active });
      await load();
    } catch (e) { alert(formatApiError(e)); }
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display font-bold text-2xl flex items-center gap-2">
            <Gift className="w-6 h-6 text-pink-600" /> Promociones
          </h1>
          <p className="text-sm text-neutral-500 mt-1">
            Gestiona descuentos con control anti-fraude por número de celular
          </p>
        </div>
        <button
          onClick={() => setEditing({ name: "", kind: "welcome_first_ride", percent: 20, is_active: true, description: "" })}
          className="px-4 py-2 bg-pink-600 text-white rounded-lg font-semibold flex items-center gap-2 hover:bg-pink-700"
          data-testid="promo-create-btn"
        >
          <Plus className="w-4 h-4" /> Nueva promo
        </button>
      </div>

      {err && <div className="chip chip-danger">{err}</div>}

      {/* Anti-fraud explainer */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
        <div className="flex items-center gap-2 font-bold text-emerald-900 mb-1">
          <ShieldCheck className="w-4 h-4" /> Control anti-fraude activado
        </div>
        <p className="text-xs text-emerald-800 leading-relaxed">
          Cada teléfono se identifica por sus dígitos (independiente del formato con espacios/guiones/prefijo).
          Un mismo número <strong>no puede reclamar la promo dos veces</strong>, aunque cree una cuenta nueva con otro correo.
          Si el número tiene <strong>cualquier carrera</strong> (no cancelada) en el sistema, tampoco recibe el descuento.
        </p>
      </div>

      {/* List */}
      {busy ? (
        <div className="p-6 text-center text-neutral-500">Cargando...</div>
      ) : promos.length === 0 ? (
        <div className="bg-neutral-50 border-2 border-dashed border-neutral-300 rounded-xl p-10 text-center text-neutral-400">
          <Gift className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <div className="text-sm font-medium">Aún no hay promociones. Crea la primera.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {promos.map((p) => (
            <div key={p.id}
                 className={`bg-white border-2 rounded-xl p-5 ${p.is_active ? "border-pink-300" : "border-neutral-200 opacity-70"}`}
                 data-testid={`promo-card-${p.id}`}>
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-bold text-lg truncate">{p.name}</div>
                    {p.is_active ? (
                      <span className="bg-green-100 text-green-700 text-[10px] font-bold px-2 py-0.5 rounded uppercase">Activa</span>
                    ) : (
                      <span className="bg-neutral-200 text-neutral-600 text-[10px] font-bold px-2 py-0.5 rounded uppercase">Inactiva</span>
                    )}
                  </div>
                  <div className="text-xs text-neutral-500 mt-0.5">
                    {p.kind === "welcome_first_ride" ? "🎉 Bienvenida — primera carrera" : p.kind}
                  </div>
                </div>
                <button onClick={() => toggleActive(p)} className="p-1.5" title={p.is_active ? "Desactivar" : "Activar"}
                        data-testid={`promo-toggle-${p.id}`}>
                  {p.is_active ? <ToggleRight className="w-8 h-8 text-pink-600" /> : <ToggleLeft className="w-8 h-8 text-neutral-400" />}
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="bg-pink-50 rounded-lg p-3 text-center">
                  <div className="text-3xl font-black text-pink-700">{p.percent}%</div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-pink-600">Descuento</div>
                </div>
                <div className="bg-neutral-50 rounded-lg p-3 text-center">
                  <div className="text-3xl font-black text-neutral-700">{p.claims_count || 0}</div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-600">Reclamos</div>
                </div>
              </div>

              {p.description && <div className="text-xs text-neutral-600 mb-3 italic">{p.description}</div>}

              <div className="flex gap-2">
                <button onClick={() => setEditing(p)} className="flex-1 py-2 border border-neutral-300 rounded-lg text-xs font-semibold hover:bg-neutral-50 flex items-center justify-center gap-1"
                        data-testid={`promo-edit-${p.id}`}>
                  <Edit2 className="w-3 h-3" /> Editar
                </button>
                <button onClick={() => setClaimsOf(p)} className="flex-1 py-2 border border-pink-300 text-pink-700 rounded-lg text-xs font-semibold hover:bg-pink-50 flex items-center justify-center gap-1"
                        data-testid={`promo-claims-${p.id}`}>
                  <Users className="w-3 h-3" /> Ver reclamos
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <PromoEditor promo={editing} onClose={() => { setEditing(null); load(); }} />}
      {claimsOf && <ClaimsModal promo={claimsOf} onClose={() => setClaimsOf(null)} />}
    </div>
  );
}

function PromoEditor({ promo, onClose }) {
  const [form, setForm] = useState({
    name: promo.name || "Bienvenida 20%",
    kind: promo.kind || "welcome_first_ride",
    percent: promo.percent ?? 20,
    is_active: promo.is_active ?? true,
    description: promo.description || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    if (!form.name.trim()) return setErr("El nombre es obligatorio");
    if (form.percent <= 0 || form.percent > 100) return setErr("El porcentaje debe estar entre 1 y 100");
    setBusy(true); setErr("");
    try {
      if (promo.id) await dataApi.updatePromotion(promo.id, form);
      else await dataApi.createPromotion(form);
      onClose();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="promo-editor-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-xl">{promo.id ? "Editar promo" : "Nueva promo"}</h2>
          <button onClick={onClose} data-testid="promo-editor-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Nombre</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                   className="office-input mt-1.5" placeholder="Bienvenida 20%" data-testid="promo-name-input"/>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Tipo</label>
            <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}
                    className="office-input mt-1.5" disabled data-testid="promo-kind-select">
              <option value="welcome_first_ride">🎉 Bienvenida (primera carrera)</option>
            </select>
            <div className="text-[11px] text-neutral-500 mt-1">Por ahora solo soportamos este tipo. Próximamente: cumpleaños, referidos, códigos.</div>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Porcentaje de descuento</label>
            <div className="relative mt-1.5">
              <input type="number" min="1" max="100" value={form.percent}
                     onChange={(e) => setForm({ ...form, percent: parseFloat(e.target.value) || 0 })}
                     className="office-input pr-10 font-mono" data-testid="promo-percent-input"/>
              <Percent className="w-4 h-4 text-neutral-400 absolute right-3 top-1/2 -translate-y-1/2" />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Descripción (opcional)</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                      rows="2" className="office-input mt-1.5" placeholder="Descuento válido solo en la primera carrera"
                      data-testid="promo-description-input"/>
          </div>

          <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-neutral-50">
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="promo-active-checkbox"/>
            <div>
              <div className="text-sm font-semibold">Activa</div>
              <div className="text-xs text-neutral-500">Si otra promo del mismo tipo está activa, se desactivará automáticamente</div>
            </div>
          </label>

          {err && <div className="chip chip-danger">{err}</div>}

          <div className="flex gap-2 pt-2">
            <button onClick={onClose} className="flex-1 py-2.5 border border-neutral-300 rounded-lg font-semibold hover:bg-neutral-50">Cancelar</button>
            <button onClick={submit} disabled={busy} className="flex-1 py-2.5 bg-pink-600 text-white rounded-lg font-bold disabled:opacity-50 hover:bg-pink-700" data-testid="promo-save-btn">
              {busy ? "Guardando..." : promo.id ? "Guardar" : "Crear promo"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ClaimsModal({ promo, onClose }) {
  const [claims, setClaims] = useState([]);
  const [busy, setBusy] = useState(true);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    dataApi.listPromoClaims(promo.id)
      .then((r) => setClaims(r.claims || []))
      .catch(() => setClaims([]))
      .finally(() => setBusy(false));
  }, [promo.id]);

  const filtered = claims.filter((c) => (c.phone || "").includes(filter) || (c.phone_digits || "").includes(filter));

  const copyAll = () => {
    const txt = filtered.map((c) => c.phone).join("\n");
    try { navigator.clipboard.writeText(txt); } catch { /* ignore */ }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="promo-claims-modal">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-bold text-xl">Reclamos de "{promo.name}"</h2>
            <div className="text-xs text-neutral-500 mt-0.5">{claims.length} números que ya usaron esta promo</div>
          </div>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>

        <div className="flex gap-2 mb-3">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Buscar por teléfono..."
                   className="office-input pl-9" data-testid="claims-filter-input"/>
          </div>
          <button onClick={copyAll} className="px-3 border rounded-lg text-xs font-semibold hover:bg-neutral-50 flex items-center gap-1"
                  data-testid="claims-copy-all">
            <Copy className="w-3.5 h-3.5" /> Copiar todos
          </button>
        </div>

        <div className="flex-1 overflow-auto border rounded-lg">
          {busy ? (
            <div className="p-6 text-center text-sm text-neutral-500">Cargando...</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-neutral-500">
              {claims.length === 0 ? "Todavía nadie ha usado esta promo" : "Sin resultados"}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-xs font-semibold text-neutral-600 uppercase sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">Teléfono</th>
                  <th className="px-3 py-2 text-left">Dígitos</th>
                  <th className="px-3 py-2 text-left">Reclamado</th>
                  <th className="px-3 py-2 text-left">Ride ID</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.id} className="border-t border-neutral-100" data-testid={`claim-row-${c.id}`}>
                    <td className="px-3 py-2 font-mono">{c.phone}</td>
                    <td className="px-3 py-2 font-mono text-xs text-neutral-500">{c.phone_digits}</td>
                    <td className="px-3 py-2 text-xs text-neutral-600">{new Date(c.claimed_at).toLocaleString("es-PE")}</td>
                    <td className="px-3 py-2 font-mono text-[10px] text-neutral-500">{(c.ride_id || "").slice(0, 8)}...</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
